import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  DocumentDuplicateIcon,
  CodeBracketIcon,
  GlobeAltIcon,
  CpuChipIcon,
  CloudIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  HomeIcon,
  SparklesIcon,
  CheckIcon,
  PaintBrushIcon,
  FilmIcon,
  ShoppingBagIcon,
  HeartIcon
} from '@heroicons/react/24/outline'
import { updateConfig } from '../api/config'

const ConfigTemplates = () => {
  const queryClient = useQueryClient()
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [applyMode, setApplyMode] = useState('theme') // 'theme' 或 'full'

  // 配置模板数据
  const templates = [
    {
      id: 'developer',
      name: '开发者工作台',
      description: '专为开发者设计的配置，包含常用开发工具和监控面板',
      icon: CodeBracketIcon,
      color: 'blue',
      category: 'professional',
      configs: {
        services: [
          {
            '开发工具': [
              {
                'GitHub': {
                  href: 'https://github.com',
                  description: '代码仓库和版本控制',
                  icon: 'mdi-github'
                }
              },
              {
                'VS Code': {
                  href: 'https://vscode.dev',
                  description: '在线代码编辑器',
                  icon: 'mdi-microsoft-visual-studio-code'
                }
              },
              {
                'Stack Overflow': {
                  href: 'https://stackoverflow.com',
                  description: '编程问答社区',
                  icon: 'mdi-stack-overflow'
                }
              }
            ]
          },
          {
            '部署与监控': [
              {
                'Docker Hub': {
                  href: 'https://hub.docker.com',
                  description: '容器镜像仓库',
                  icon: 'mdi-docker'
                }
              },
              {
                'Grafana': {
                  href: 'http://localhost:3001',
                  description: '监控面板',
                  icon: 'mdi-chart-line'
                }
              },
              {
                'Glances': {
                  href: 'http://localhost:61208',
                  description: '系统资源监控',
                  icon: 'mdi-monitor-dashboard',
                  widget: {
                    type: 'glances',
                    url: 'http://localhost:61208'
                  }
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'google',
              target: '_blank',
              href: 'https://google.com'
            }
          },
          {
            resources: {
              cpu: true,
              memory: true,
              disk: '/',
              href: 'http://localhost:3001',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'xl'
            }
          }
        ],
        bookmarks: [
          {
            '开发资源': [
              {
                'MDN Web Docs': [
                  {
                    href: 'https://developer.mozilla.org',
                    description: 'Web 开发文档',
                    icon: 'mdi-firefox'
                  }
                ]
              },
              {
                'Can I Use': [
                  {
                    href: 'https://caniuse.com',
                    description: '浏览器兼容性查询',
                    icon: 'mdi-web'
                  }
                ]
              }
            ]
          }
        ],
        settings: {
          title: '开发者工作台',
          theme: 'dark',
          color: 'blue',
          headerStyle: 'clean',
          target: '_blank'
        }
      }
    },
    {
      id: 'personal',
      name: '个人导航页',
      description: '适合个人使用的简洁导航页面，包含常用网站和工具',
      icon: HomeIcon,
      color: 'green',
      category: 'personal',
      configs: {
        services: [
          {
            '常用网站': [
              {
                'Google': {
                  href: 'https://google.com',
                  description: '搜索引擎',
                  icon: 'mdi-google'
                }
              },
              {
                'YouTube': {
                  href: 'https://youtube.com',
                  description: '视频平台',
                  icon: 'mdi-youtube'
                }
              },
              {
                'Gmail': {
                  href: 'https://gmail.com',
                  description: '邮箱服务',
                  icon: 'mdi-gmail'
                }
              }
            ]
          },
          {
            '社交媒体': [
              {
                'Twitter': {
                  href: 'https://twitter.com',
                  description: '社交网络',
                  icon: 'mdi-twitter'
                }
              },
              {
                'Instagram': {
                  href: 'https://instagram.com',
                  description: '图片分享',
                  icon: 'mdi-instagram'
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'google',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'lg'
            }
          },
          {
            greeting: {
              text: '欢迎回来！'
            }
          }
        ],
        bookmarks: [
          {
            '常用工具': [
              {
                '网盘服务': [
                  {
                    href: 'https://drive.google.com',
                    description: 'Google Drive',
                    icon: 'mdi-google-drive'
                  }
                ]
              }
            ]
          }
        ],
        settings: {
          title: '我的导航页',
          theme: 'auto',
          color: 'green',
          headerStyle: 'boxed',
          target: '_blank'
        }
      }
    },
    {
      id: 'enterprise',
      name: '企业工作台',
      description: '适合企业环境的配置，包含办公工具和企业应用',
      icon: BriefcaseIcon,
      color: 'purple',
      category: 'professional',
      configs: {
        services: [
          {
            '办公工具': [
              {
                'Microsoft 365': {
                  href: 'https://office.com',
                  description: '办公套件',
                  icon: 'mdi-microsoft-office'
                }
              },
              {
                'Teams': {
                  href: 'https://teams.microsoft.com',
                  description: '团队协作',
                  icon: 'mdi-microsoft-teams'
                }
              },
              {
                'Outlook': {
                  href: 'https://outlook.com',
                  description: '企业邮箱',
                  icon: 'mdi-microsoft-outlook'
                }
              }
            ]
          },
          {
            '企业应用': [
              {
                'Slack': {
                  href: 'https://slack.com',
                  description: '团队沟通',
                  icon: 'mdi-slack'
                }
              },
              {
                'Notion': {
                  href: 'https://notion.so',
                  description: '知识管理',
                  icon: 'mdi-note-text'
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'bing',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'xl'
            }
          },
          {
            greeting: {
              text: '开始高效工作！'
            }
          }
        ],
        bookmarks: [
          {
            '企业资源': [
              {
                '人力资源': [
                  {
                    href: 'https://hr.company.com',
                    description: 'HR 系统',
                    icon: 'mdi-account-group'
                  }
                ]
              }
            ]
          }
        ],
        settings: {
          title: '企业工作台',
          theme: 'light',
          color: 'purple',
          headerStyle: 'underlined',
          target: '_blank'
        }
      }
    },
    {
      id: 'gaming',
      name: '游戏娱乐中心',
      description: '游戏爱好者的专属导航页面，包含游戏平台和相关工具',
      icon: CpuChipIcon,
      color: 'red',
      category: 'entertainment',
      configs: {
        services: [
          {
            '游戏平台': [
              {
                'Steam': {
                  href: 'https://store.steampowered.com',
                  description: 'PC游戏平台',
                  icon: 'mdi-steam'
                }
              },
              {
                'Epic Games': {
                  href: 'https://store.epicgames.com',
                  description: 'Epic游戏商店',
                  icon: 'mdi-gamepad-variant'
                }
              },
              {
                'Xbox Game Pass': {
                  href: 'https://www.xbox.com/xbox-game-pass',
                  description: 'Xbox订阅服务',
                  icon: 'mdi-microsoft-xbox'
                }
              }
            ]
          },
          {
            '游戏资讯': [
              {
                'IGN': {
                  href: 'https://ign.com',
                  description: '游戏新闻资讯',
                  icon: 'mdi-newspaper'
                }
              },
              {
                'GameSpot': {
                  href: 'https://gamespot.com',
                  description: '游戏评测资讯',
                  icon: 'mdi-star'
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'google',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'xl'
            }
          },
          {
            greeting: {
              text: '准备开始游戏！'
            }
          }
        ],
        bookmarks: [
          {
            '游戏工具': [
              {
                'Steam Calculator': [
                  {
                    href: 'https://steamcalculator.com',
                    description: 'Steam账户价值计算',
                    icon: 'mdi-calculator'
                  }
                ]
              }
            ]
          }
        ],
        settings: {
          title: '游戏娱乐中心',
          theme: 'dark',
          color: 'red',
          headerStyle: 'clean',
          target: '_blank'
        }
      }
    },
    {
      id: 'media',
      name: '影音娱乐站',
      description: '影音爱好者的专属页面，包含视频、音乐和娱乐平台',
      icon: FilmIcon,
      color: 'yellow',
      category: 'entertainment',
      configs: {
        services: [
          {
            '视频平台': [
              {
                'Netflix': {
                  href: 'https://netflix.com',
                  description: '在线视频流媒体',
                  icon: 'mdi-netflix'
                }
              },
              {
                'YouTube': {
                  href: 'https://youtube.com',
                  description: '视频分享平台',
                  icon: 'mdi-youtube'
                }
              },
              {
                'Bilibili': {
                  href: 'https://bilibili.com',
                  description: '弹幕视频网站',
                  icon: 'mdi-television-play'
                }
              }
            ]
          },
          {
            '音乐平台': [
              {
                'Spotify': {
                  href: 'https://spotify.com',
                  description: '音乐流媒体服务',
                  icon: 'mdi-spotify'
                }
              },
              {
                'Apple Music': {
                  href: 'https://music.apple.com',
                  description: 'Apple音乐服务',
                  icon: 'mdi-apple'
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'google',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'xl'
            }
          },
          {
            greeting: {
              text: '享受娱乐时光！'
            }
          }
        ],
        bookmarks: [
          {
            '影音资源': [
              {
                'IMDb': [
                  {
                    href: 'https://imdb.com',
                    description: '电影数据库',
                    icon: 'mdi-movie'
                  }
                ]
              }
            ]
          }
        ],
        settings: {
          title: '影音娱乐站',
          theme: 'dark',
          color: 'yellow',
          headerStyle: 'clean',
          target: '_blank'
        }
      }
    },
    {
      id: 'shopping',
      name: '购物导航站',
      description: '购物爱好者的专属页面，包含各大电商平台和优惠信息',
      icon: ShoppingBagIcon,
      color: 'green',
      category: 'lifestyle',
      configs: {
        services: [
          {
            '电商平台': [
              {
                '淘宝': {
                  href: 'https://taobao.com',
                  description: '综合购物平台',
                  icon: 'mdi-shopping'
                }
              },
              {
                '京东': {
                  href: 'https://jd.com',
                  description: '品质购物平台',
                  icon: 'mdi-store'
                }
              },
              {
                'Amazon': {
                  href: 'https://amazon.com',
                  description: '全球购物平台',
                  icon: 'mdi-amazon'
                }
              }
            ]
          },
          {
            '优惠信息': [
              {
                '什么值得买': {
                  href: 'https://smzdm.com',
                  description: '购物决策平台',
                  icon: 'mdi-tag'
                }
              },
              {
                '优惠券': {
                  href: 'https://coupon.com',
                  description: '优惠券聚合',
                  icon: 'mdi-ticket-percent'
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'google',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'xl'
            }
          },
          {
            greeting: {
              text: '开始愉快购物！'
            }
          }
        ],
        bookmarks: [
          {
            '购物工具': [
              {
                '价格历史': [
                  {
                    href: 'https://price-tracker.com',
                    description: '商品价格追踪',
                    icon: 'mdi-chart-line'
                  }
                ]
              }
            ]
          }
        ],
        settings: {
          title: '购物导航站',
          theme: 'light',
          color: 'green',
          headerStyle: 'underlined',
          target: '_blank'
        }
      }
    },
    {
      id: 'minimalist',
      name: '极简主义',
      description: '简洁优雅的极简风格，专注于核心功能',
      icon: SparklesIcon,
      color: 'purple',
      category: 'style',
      configs: {
        services: [
          {
            '核心工具': [
              {
                'Google': {
                  href: 'https://google.com',
                  description: '搜索引擎',
                  icon: 'mdi-google'
                }
              },
              {
                'Gmail': {
                  href: 'https://gmail.com',
                  description: '邮箱服务',
                  icon: 'mdi-gmail'
                }
              },
              {
                'Calendar': {
                  href: 'https://calendar.google.com',
                  description: '日历应用',
                  icon: 'mdi-calendar'
                }
              }
            ]
          }
        ],
        widgets: [
          {
            search: {
              provider: 'google',
              target: '_blank'
            }
          },
          {
            datetime: {
              text_size: 'xl'
            }
          }
        ],
        bookmarks: [],
        settings: {
          title: '极简导航',
          theme: 'light',
          color: 'purple',
          headerStyle: 'clean',
          target: '_blank'
        }
      }
    }
  ]

  // 应用模板配置
  const applyTemplateMutation = useMutation({
    mutationFn: async ({ template, mode }) => {
      const { configs } = template

      if (mode === 'theme') {
        // 只应用主题设置
        if (configs.settings) {
          await updateConfig('settings', configs.settings)
        }
      } else {
        // 应用完整配置
        for (const [configType, configData] of Object.entries(configs)) {
          await updateConfig(configType, configData)
        }
      }
    },
    onSuccess: (_, { mode }) => {
      queryClient.invalidateQueries(['config'])
      const message = mode === 'theme' ? '主题设置已成功应用！' : '配置模板已成功应用！'
      toast.success(message)
      setSelectedTemplate(null)
      setShowPreview(false)
    },
    onError: (error) => {
      toast.error(`应用模板失败: ${error.message}`)
    },
  })

  const handleApplyTemplate = (template, mode = applyMode) => {
    const confirmMessage = mode === 'theme'
      ? `确定要应用"${template.name}"的主题设置吗？\n\n这只会更新主题相关设置，不会影响现有的服务、书签和小部件数据。`
      : `确定要应用"${template.name}"模板吗？\n\n这将覆盖当前的所有配置。建议先创建备份。`

    if (window.confirm(confirmMessage)) {
      applyTemplateMutation.mutate({ template, mode })
    }
  }

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template)
    setShowPreview(true)
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-3xl font-bold text-white">配置模板</h1>
        <p className="mt-2 text-gray-400">
          选择预设的配置模板，快速搭建你的 Homepage 导航页
        </p>
      </div>

      {/* 应用模式选择 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <PaintBrushIcon className="h-5 w-5 mr-2" />
            应用模式
          </h3>
        </div>
        <div className="card-content">
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="applyMode"
                value="theme"
                checked={applyMode === 'theme'}
                onChange={(e) => setApplyMode(e.target.value)}
                className="mr-2"
              />
              <span className="text-white">仅应用主题设置</span>
              <span className="text-sm text-gray-400 ml-2">（保留现有数据，只更新主题、颜色等设置）</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="applyMode"
                value="full"
                checked={applyMode === 'full'}
                onChange={(e) => setApplyMode(e.target.value)}
                className="mr-2"
              />
              <span className="text-white">完整应用模板</span>
              <span className="text-sm text-gray-400 ml-2">（覆盖所有配置数据）</span>
            </label>
          </div>
        </div>
      </div>

      {/* 模板分类 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => {
          const IconComponent = template.icon
          const colorClasses = {
            blue: 'border-blue-500/30 bg-blue-900/20',
            green: 'border-green-500/30 bg-green-900/20',
            purple: 'border-purple-500/30 bg-purple-900/20',
            red: 'border-red-500/30 bg-red-900/20',
            yellow: 'border-yellow-500/30 bg-yellow-900/20'
          }

          return (
            <div
              key={template.id}
              className={`card border ${colorClasses[template.color]}`}
            >
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg bg-${template.color}-500/20`}>
                  <IconComponent className={`h-6 w-6 text-${template.color}-400`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-white">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {template.description}
                  </p>
                </div>
              </div>

              <div className="flex space-x-2 mt-4">
                <button
                  onClick={() => handlePreviewTemplate(template)}
                  className="btn-secondary flex-1"
                >
                  预览配置
                </button>
                <button
                  onClick={() => handleApplyTemplate(template)}
                  disabled={applyTemplateMutation.isLoading}
                  className="btn-primary flex-1"
                >
                  {applyTemplateMutation.isLoading ? '应用中...' : '应用模板'}
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* 预览模态框 */}
      {showPreview && selectedTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowPreview(false)} />
            
            <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">
                  预览配置：{selectedTemplate.name}
                </h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6 max-h-96 overflow-y-auto">
                {Object.entries(selectedTemplate.configs).map(([configType, configData]) => (
                  <div key={configType} className="bg-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-white mb-3 capitalize">
                      {configType === 'services' ? '服务配置' : 
                       configType === 'widgets' ? '小部件配置' : 
                       configType === 'bookmarks' ? '书签配置' : 
                       configType === 'settings' ? '基础设置' : configType}
                    </h4>
                    <pre className="text-sm text-gray-300 bg-gray-800 rounded p-3 overflow-x-auto">
                      {JSON.stringify(configData, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowPreview(false)}
                  className="btn-secondary"
                >
                  关闭
                </button>
                <button
                  onClick={() => {
                    setShowPreview(false)
                    handleApplyTemplate(selectedTemplate)
                  }}
                  disabled={applyTemplateMutation.isLoading}
                  className="btn-primary"
                >
                  应用此模板
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConfigTemplates
